import React, { FC, useMemo, useState } from 'react';
import {
	Chip,
	IconButton,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';
import styled from 'styled-components';
import { TestResultVariable } from './whatsapp.types';

type Props = {
	/** The template BODY text with {{n}} placeholders. */
	bodyText: string;
	/** How many distinct placeholders the backend counted. */
	placeholderCount: number;
	/** NAMED-parameter templates: the placeholder name per slot, body order. */
	placeholderNames?: string[];
	/** Template language code ('ar', 'en_US', ...) — an RTL language right-aligns the preview. */
	templateLanguage?: string;
	variables: TestResultVariable[];
	/**
	 * One entry per placeholder: a variable key, or a pattern mixing text and
	 * {variable} tokens ("{student_first_name} {student_last_name}"). '' =
	 * unassigned.
	 */
	mapping: string[];
	onChange: (mapping: string[]) => void;
	disabled?: boolean;
};

/** `{variable_key}` token inside a slot pattern — same grammar as both backends. */
const TOKEN = /\{\s*([a-z0-9_]+)\s*\}/g;

const isPattern = (slot: string) => slot.includes('{');

/** "..." = literal text sent as-is — same convention as both backends. */
const isQuotedLiteral = (slot: string) =>
	slot.length >= 2 && slot.startsWith('"') && slot.endsWith('"');

/** Variable keys referenced by a pattern (no String.matchAll — keeps older browsers happy). */
const tokensOf = (entry: string): string[] => {
	const tokens: string[] = [];
	entry.replace(TOKEN, (_, key: string) => {
		tokens.push(key);
		return '';
	});
	return tokens;
};

/**
 * One row per {{n}} placeholder in the chosen template's body. A slot is
 * either a single variable picked from the list, or — via the pencil — a
 * custom pattern where variables are combined with literal text, e.g.
 * "{student_first_name} {student_last_name}" or "{score} / {total}".
 * The preview below substitutes each variable's sample so the admin can
 * read the message the parent or student will actually get.
 */
const TemplateMappingTable: FC<Props> = ({
	bodyText,
	placeholderCount,
	placeholderNames,
	templateLanguage,
	variables,
	mapping,
	onChange,
	disabled,
}) => {
	const byKey = useMemo(
		() => new Map(variables.map((variable) => [variable.key, variable])),
		[variables]
	);
	// Rows switched to custom mode by hand (a stored pattern is custom on its own).
	const [customSlots, setCustomSlots] = useState<Record<number, boolean>>({});

	const renderSlot = (slot: string | undefined): string | null => {
		const entry = (slot ?? '').trim();
		if (!entry) return null;
		if (isQuotedLiteral(entry)) return entry.slice(1, -1);
		if (!isPattern(entry)) return byKey.get(entry)?.sample ?? null;
		let unknown = false;
		const rendered = entry.replace(TOKEN, (_, key: string) => {
			const variable = byKey.get(key);
			if (!variable) unknown = true;
			return variable ? variable.sample : '?';
		});
		return unknown ? null : rendered;
	};

	const preview = useMemo(
		() =>
			bodyText.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (match, token) => {
				const index = /^\d+$/.test(token)
					? Number(token) - 1
					: placeholderNames?.indexOf(token) ?? -1;
				return index >= 0 ? renderSlot(mapping[index]) ?? match : match;
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bodyText, mapping, byKey, placeholderNames]
	);

	const setSlot = (slot: number, value: string) => {
		const next = [...mapping];
		while (next.length < placeholderCount) next.push('');
		next[slot] = value;
		onChange(next);
	};

	const slotProblem = (slot: string | undefined): string | null => {
		const entry = (slot ?? '').trim();
		if (!entry) return 'Choose a variable';
		if (isQuotedLiteral(entry))
			return entry.includes('{') || entry.includes('}')
				? 'A quoted literal cannot contain braces'
				: null;
		if (!isPattern(entry)) return byKey.has(entry) ? null : 'Unknown variable';
		const tokens = tokensOf(entry);
		if (!tokens.length) return 'Add at least one {variable}';
		const unknown = tokens.filter((token) => !byKey.has(token));
		return unknown.length ? `Unknown: ${unknown.join(', ')}` : null;
	};

	if (placeholderCount === 0) {
		return (
			<Typography variant="caption" color="textSecondary">
				This template has no variables — the same text is sent to everyone.
			</Typography>
		);
	}

	return (
		<>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell width="26%">Placeholder</TableCell>
						<TableCell>Value</TableCell>
						<TableCell width="48px" />
					</TableRow>
				</TableHead>
				<TableBody>
					{Array.from({ length: placeholderCount }, (_, slot) => {
						const value = mapping[slot] ?? '';
						const custom = customSlots[slot] || isPattern(value) || isQuotedLiteral(value);
						const problem = slotProblem(value);
						return (
							<TableRow key={slot}>
								<TableCell>
									<Placeholder>
										{placeholderNames?.[slot]
											? `{{${placeholderNames[slot]}}}`
											: `{{${slot + 1}}}`}
									</Placeholder>
								</TableCell>
								<TableCell>
									{custom ? (
										<>
											<TextField
												fullWidth
												size="small"
												value={value}
												disabled={disabled}
												error={!!problem}
												placeholder="{student_first_name} {student_last_name}"
												helperText={
													problem ??
													'Text plus {variable} tokens; click a variable below to insert it. Wrap the whole value in "double quotes" to send fixed text.'
												}
												onChange={(event) => setSlot(slot, event.target.value)}
											/>
											<Inserts>
												{variables.map((variable) => (
													<Chip
														key={variable.key}
														size="small"
														variant="outlined"
														label={variable.label}
														disabled={disabled}
														onClick={() =>
															setSlot(slot, `${value}${value && !value.endsWith(' ') ? ' ' : ''}{${variable.key}}`)
														}
													/>
												))}
											</Inserts>
										</>
									) : (
										<TextField
											select
											fullWidth
											size="small"
											value={byKey.has(value) ? value : ''}
											disabled={disabled}
											error={!!problem}
											onChange={(event) => setSlot(slot, event.target.value)}
										>
											<MenuItem value="">
												<em>Choose a variable…</em>
											</MenuItem>
											{variables.map((variable) => (
												<MenuItem key={variable.key} value={variable.key}>
													{variable.label}
													<Sample>e.g. {variable.sample}</Sample>
												</MenuItem>
											))}
										</TextField>
									)}
								</TableCell>
								<TableCell align="center">
									<Tooltip title={custom ? 'Back to a single variable' : 'Custom: combine variables with text'}>
										<span>
											<IconButton
												size="small"
												disabled={disabled}
												onClick={() => {
													if (custom) {
														// Leaving custom mode: keep a bare key if that is all it was.
														setCustomSlots({ ...customSlots, [slot]: false });
														if (isPattern(value) || isQuotedLiteral(value)) setSlot(slot, '');
													} else {
														setCustomSlots({ ...customSlots, [slot]: true });
														if (value) setSlot(slot, `{${value}}`);
													}
												}}
											>
												{custom ? <ListIcon fontSize="small" /> : <EditIcon fontSize="small" />}
											</IconButton>
										</span>
									</Tooltip>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
			<Preview $rtl={!!templateLanguage && /^(ar|he|fa|ur)/i.test(templateLanguage)}>
				{preview}
			</Preview>
		</>
	);
};

const Placeholder = styled.code`
	background: rgba(90, 192, 252, 0.12);
	border-radius: 3px;
	padding: 0.1rem 0.35rem;
	/* Named placeholders ({{educational_year}}) are long unbreakable tokens:
	   show them in full, wrapped, instead of spilling out of the column. */
	display: inline-block;
	max-width: 100%;
	white-space: normal;
	overflow-wrap: anywhere;
`;

const Sample = styled.span`
	margin-inline-start: 0.5rem;
	color: rgba(0, 0, 0, 0.5);
	font-size: 0.75rem;
`;

const Inserts = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.25rem;
	margin-top: 0.35rem;
`;

const Preview = styled.pre<{ $rtl?: boolean }>`
	margin: 0;
	padding: 0.75rem 1rem;
	background: #f6f8fa;
	border-radius: 6px;
	white-space: pre-wrap;
	font-family: inherit;
	font-size: 0.875rem;
	line-height: 1.5;
	/* Each line resolves its own bidi order from its first strong character
	   — the way WhatsApp renders it — so a Latin-only {{token}} line can
	   never flip its edge braces inside an Arabic template. Alignment stays
	   uniform, on the template language's side. */
	unicode-bidi: plaintext;
	text-align: ${({ $rtl }) => ($rtl ? 'right' : 'left')};
`;

export default TemplateMappingTable;
